import React, { useCallback, useMemo, useState } from "react";
import GlobalBadge from "../../../../global/GlobalBadge";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../../global/directory";
import {
  TRACKING_DONATIONS_COLUMN_DEFS,
  TRACKING_DONATIONS_VISIBILITY_KEY,
  DONOR_DIRECTORY_PAGE_SIZE,
  buildValueOptionsFromRows,
  createInitialColumnVisibility,
  defaultDirectoryFilters,
  filterDirectoryRows,
  paginateList,
  trackingDonationCellValue,
  trackingDonationSearchableText,
} from "../../utils/donorPortalDirectoryHelpers";
import { isDonationApproved } from "../../utils/donationTrackingFormatters";

export default function DonationTrackingDonationsDirectorySection({
  loading,
  donations,
}) {
  const rows = Array.isArray(donations) ? donations : [];
  const [filters, setFilters] = useState(defaultDirectoryFilters);
  const [page, setPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    createInitialColumnVisibility(
      TRACKING_DONATIONS_VISIBILITY_KEY,
      TRACKING_DONATIONS_COLUMN_DEFS
    )
  );

  const filtered = useMemo(
    () =>
      filterDirectoryRows(rows, filters, {
        getSearchableText: trackingDonationSearchableText,
        getColumnValue: trackingDonationCellValue,
      }),
    [rows, filters]
  );

  const { page: safePage, totalPages, total, slice } = useMemo(
    () => paginateList(filtered, page, DONOR_DIRECTORY_PAGE_SIZE),
    [filtered, page]
  );

  const valueFilterOptions = useMemo(() => {
    const col = filters.column;
    if (!col) return [];
    return buildValueOptionsFromRows(rows, col, trackingDonationCellValue);
  }, [filters.column, rows]);

  const visibleDataColumns = useMemo(
    () => TRACKING_DONATIONS_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility]
  );

  const tableHeaders = useMemo(
    () => visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
    [visibleDataColumns]
  );

  const getExportData = useCallback(() => {
    const headers = visibleDataColumns.map((c) => c.label);
    const dataRows = filtered.map((row) =>
      visibleDataColumns.map((c) => trackingDonationCellValue(c.id, row))
    );
    return { headers, rows: dataRows };
  }, [filtered, visibleDataColumns]);

  const showRows = !loading && slice.length > 0;

  return (
    <div className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Cash donations</h3>
        <p className="user-builder-panel__hint">
          Track verification status for each cash donation you have made.
        </p>
      </div>

      <GlobalDirectoryShell
        filters={
          <GlobalDirectoryFiltersSection
            disabled={loading}
            searchLabel="Search"
            searchPlaceholder="Receipt, purpose, mode, status…"
            searchValue={filters.search}
            onSearchChange={(v) => {
              setFilters((f) => ({ ...f, search: v }));
              setPage(1);
            }}
            columnLabel="Column"
            columnValue={filters.column}
            onColumnChange={(v) => {
              setFilters((f) => ({ ...f, column: v, value: "" }));
              setPage(1);
            }}
            gridColumnDefs={TRACKING_DONATIONS_COLUMN_DEFS}
            valueLabel="Value"
            valueValue={filters.value}
            onValueChange={(v) => {
              setFilters((f) => ({ ...f, value: v }));
              setPage(1);
            }}
            valueOptions={valueFilterOptions}
            onClear={() => {
              setFilters(defaultDirectoryFilters());
              setPage(1);
            }}
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={TRACKING_DONATIONS_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
                storageKey={TRACKING_DONATIONS_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getExportData}
                filenameBase="donation_tracking_cash"
              />
            }
          />
        }
        grid={
          <GlobalDirectoryGridSection
            loading={loading}
            loadingMessage="Loading donations…"
            headers={tableHeaders}
            isEmpty={!loading && filtered.length === 0}
            emptyMessage="No donations recorded yet."
            tableAriaLabel="Cash donation tracking directory"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {showRows
              ? slice.map((row) => {
                  const approved = isDonationApproved(row);
                  return (
                    <tr key={row.id}>
                      {visibleDataColumns.map((col) => (
                        <td key={col.id}>
                          {col.id === "status" ? (
                            <GlobalBadge
                              active={approved}
                              activeText="Approved"
                              inactiveText="Pending"
                            />
                          ) : (
                            trackingDonationCellValue(col.id, row)
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              : null}
          </GlobalDirectoryGridSection>
        }
        pagination={
          !loading && filtered.length > 0 ? (
            <GlobalDirectoryPaginationSection
              page={safePage}
              totalPages={totalPages}
              total={total}
              pageSize={DONOR_DIRECTORY_PAGE_SIZE}
              onPageChange={setPage}
              label="Cash donations pagination"
            />
          ) : null
        }
      />
    </div>
  );
}
